import { useState, useEffect, useCallback } from 'react';
import { getAvatarUrl } from '../services/supabase/storage';

/**
 * Custom hook to fetch and cache avatar signed URLs
 * Automatically tries multiple extensions and refreshes URLs before they expire
 * @param {string} userId - User ID
 * @param {number} expiresIn - URL validity in seconds (default: 3600 = 1h)
 * @returns {Object} - { url, loading, error, refresh }
 */
export function useAvatarUrl(userId, expiresIn = 3600) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUrl = useCallback(async () => {
    if (!userId) {
      setUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getAvatarUrl(userId, expiresIn);

      if (result.error) {
        // Don't set error for missing files - this is expected for new users
        setUrl(null);
        setError(null);
      } else {
        setUrl(result.url);
      }
    } catch (err) {
      console.error('Error fetching avatar URL:', err);
      setError(err.message);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [userId, expiresIn]);

  useEffect(() => {
    fetchUrl();

    // Disable auto-refresh for now to reduce API calls
    // TODO: Re-enable once we optimize getAvatarUrl
    // const refreshInterval = (expiresIn * 1000 * 0.9);
    // const interval = setInterval(fetchUrl, refreshInterval);
    // return () => clearInterval(interval);
  }, [fetchUrl]);

  return {
    url,
    loading,
    error,
    refresh: fetchUrl,
  };
}
